export const VBZ_SUPPLIER_ID = "62bc0ef44de9282bc818daf8";
export const TNG_SUPPLIER_ID = "67ef5923435af5b8fe3f327f";

export const VBZ_TNG_SYSTEM_OPTION_BLOCKED_MESSAGE =
  "Delete system-generated delivery options and create a manual option with TNG for this split.";

export interface DeliveryOptionGuardFields {
  _id?: string;
  isSystemGenerated?: boolean | null;
  isDeleted?: boolean | null;
}

function normalizeSupplierId(id: unknown): string | undefined {
  if (id == null) {
    return undefined;
  }
  if (typeof id === "object") {
    const obj = id as { $oid?: unknown; _id?: unknown };
    const nested = obj.$oid ?? obj._id;
    if (nested != null) {
      const nestedId = String(nested).trim().toLowerCase();
      return nestedId || undefined;
    }
  }
  const str = String(id).trim().toLowerCase();
  return str && str !== "[object object]" ? str : undefined;
}

export function resolveAssociatedSupplierIdFromOrder(
  orderData?:
    | {
        customer?: {
          supplierAssociations?: Array<{ supplierId?: unknown }>;
        };
      }
    | null,
): string | undefined {
  const raw = orderData?.customer?.supplierAssociations?.[0]?.supplierId;
  return normalizeSupplierId(raw);
}

export function isVbzCustomerTngSplitCase(params: {
  associatedSupplierId?: string | null;
  splitSupplierId?: string | null;
}): boolean {
  const associated = normalizeSupplierId(params.associatedSupplierId);
  const split = normalizeSupplierId(params.splitSupplierId);
  if (!associated || !split) {
    return false;
  }
  return associated === VBZ_SUPPLIER_ID && split === TNG_SUPPLIER_ID;
}

export function isVbzCustomerTngSplitFromOrder(
  orderData: Parameters<typeof resolveAssociatedSupplierIdFromOrder>[0],
  splitSupplierId?: string | null,
): boolean {
  return isVbzCustomerTngSplitCase({
    associatedSupplierId: resolveAssociatedSupplierIdFromOrder(orderData),
    splitSupplierId,
  });
}

export function isSystemGeneratedDeliveryOption(option: {
  isSystemGenerated?: boolean | null;
}): boolean {
  return option.isSystemGenerated !== false;
}

export function getActiveDeliveryOptions<
  T extends DeliveryOptionGuardFields,
>(deliveryOptions?: T[] | null): T[] {
  return (deliveryOptions ?? []).filter((option) => !option.isDeleted);
}

export function hasActiveSystemGeneratedOptions(
  deliveryOptions?: DeliveryOptionGuardFields[] | null,
): boolean {
  return getActiveDeliveryOptions(deliveryOptions).some(
    isSystemGeneratedDeliveryOption,
  );
}

export function isVbzTngSplitSelectionBlocked(
  supplier: {
    supplierId?: string | null;
    deliveryOptions?: DeliveryOptionGuardFields[];
  },
  orderData: Parameters<typeof resolveAssociatedSupplierIdFromOrder>[0],
  selection?: {
    customerAction?: string;
    deliveryDetails?: {
      selectionType?: string;
      selectedDeliveryOptionId?: string;
    };
  },
): boolean {
  if (
    !isVbzCustomerTngSplitFromOrder(orderData, supplier.supplierId ?? undefined)
  ) {
    return false;
  }

  if (hasActiveSystemGeneratedOptions(supplier.deliveryOptions)) {
    return true;
  }

  if (
    selection?.customerAction === "confirm" &&
    selection.deliveryDetails?.selectionType === "option" &&
    selection.deliveryDetails.selectedDeliveryOptionId
  ) {
    const selected = getActiveDeliveryOptions(supplier.deliveryOptions).find(
      (option) =>
        option._id === selection.deliveryDetails?.selectedDeliveryOptionId,
    );
    if (selected && isSystemGeneratedDeliveryOption(selected)) {
      return true;
    }
  }

  return false;
}
